import { AttributeType, generateId, GeometryType } from "@evergis/api";
import { IconButton } from "@evergis/ui";
import React, { FC, useCallback, useMemo } from "react";
import { DropResult } from "react-beautiful-dnd";
import { useTranslation } from "react-i18next";
import { GEOMETRY_ATTRIBUTE } from "../../../evergis/constants";
import { reoderList } from "../../../utils/array";
import { ActionsDivider } from "../DialogComponents/Attributes/styled";
import { StyledDraggableList } from "../EditResource/components/Project/styled";
import { StyledAddSystemAttributes } from "../EditResource/components/QueryLayerResource/styled";
import { EqlAttribute } from "../EditResource/components/QueryLayerResource/types";
import { TableAttributesContainer } from "../SelectFiles/Edit/Table/styled";
import { StyledActionsButtonsGroup } from "../SelectFiles/styled";
import { useNewLayerCreate } from "./hooks/useNewLayerCreate";
import { NewLayerAttribute } from "./NewLayerAttribute";
import { useNewLayerContext } from "./newLayerContext";
import { NewLayerWizardStep } from "./NewLayerWizardStep";

export const NewLayerAttributes: FC = () => {
  const {
    attributes,
    setAttributes,
    updateAttribute,
    deleteAttribute,
    addAttribute,
    sysAttributes,
    addSysAttributes,
    deleteSysAttribute,
    attributesConfiguration,
    setDisplayField,
    geometryType,
  } = useNewLayerContext();

  const { t } = useTranslation("common");

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const attributeNames = useMemo(() => attributes.map(({ attributeName }) => attributeName), [attributes.length]);

  const onDragEnd = useCallback(
    ({ source, destination }: DropResult) => {
      if (!destination) return;
      const newAttrs = reoderList(attributes, source.index, destination.index);

      setAttributes(newAttrs);
    },
    [attributes, setAttributes],
  );

  const onAddNewAttribute = useCallback(() => {
    addAttribute({
      id: generateId(),
      isNew: true,
      attributeName: "",
      alias: "",
      type: AttributeType.String,
      columnName: "",
      isDisplayed: true,
      isEditable: true,
    });
  }, [addAttribute]);

  const onCreateLayer = useNewLayerCreate();

  return (
    <NewLayerWizardStep
      nextStepDisabled={attributes.length < 1}
      extraActions={
        <StyledActionsButtonsGroup>
          <IconButton kind={"feature_add"} onClick={onAddNewAttribute}>
            {t("actions.add")}
          </IconButton>
          <ActionsDivider vertical />
          <StyledAddSystemAttributes
            geometryType={geometryType as GeometryType}
            geometryAttributeName={GEOMETRY_ATTRIBUTE}
            sysAttributes={sysAttributes}
            addSysAttributes={addSysAttributes}
          />
        </StyledActionsButtonsGroup>
      }
      onNextStep={onCreateLayer}
    >
      <TableAttributesContainer>
        <StyledDraggableList
          onDragEnd={onDragEnd}
          id={"attributes-list"}
          items={attributes as unknown as Record<string, unknown>[]}
          getId={item => (item as unknown as EqlAttribute).id}
        >
          {(attribute, provided) => (
            <NewLayerAttribute
              draggableProvided={provided}
              attribute={attribute as unknown as EqlAttribute}
              updateAttribute={updateAttribute}
              deleteAttribute={deleteAttribute}
              deleteSysAttribute={deleteSysAttribute}
              displayField={attributesConfiguration.titleAttribute}
              setDisplayField={setDisplayField}
              geometryType={geometryType as GeometryType}
              attributeNames={attributeNames}
            />
          )}
        </StyledDraggableList>
      </TableAttributesContainer>
    </NewLayerWizardStep>
  );
};
